import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Target, Play, CheckCircle2, Loader2, Trophy, Sparkles, ChevronRight, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';

// FASE QUIZZES · PASO 2 — Pantalla del alumno "🎯 Mis Quizzes".
// Consume exclusivamente GET /quiz-assignments/mine (enriquecido en Paso 2
// con quiz_title, quiz_type y activity_count). NO muestra puntos, intentos,
// raw_score, porcentajes ni IDs. La tarjeta cambia según el status:
//   pending      → botón "Comenzar"     (placeholder futura ruta runner)
//   in_progress  → progreso "X / Y" + "Continuar"  (placeholder)
//   completed    → 🏆 + final_score + "Ver resultado" (placeholder controlado)
// Los placeholders no crean rutas nuevas: muestran un toast informativo para
// no romper la interfaz. El Runner real llega en Paso 3.

const TABS = [
  { key: 'pending', label: 'Pendientes', empty: 'No tienes Quizzes pendientes.' },
  { key: 'in_progress', label: 'En progreso', empty: 'No tienes Quizzes en progreso.' },
  { key: 'completed', label: 'Completados', empty: 'Todavía no has completado ningún Quiz.' },
];

const QUIZ_TYPE_LABEL = {
  practice: 'Práctica',
  evaluation: 'Evaluación',
  module_exam: 'Examen de módulo',
};

function QuizCard({ asg, onAction }) {
  const status = asg.status || 'pending';
  const title = asg.quiz_title || 'Quiz';
  const total = Number(asg.activity_count || 0);
  const current = Math.max(0, Math.min(total, Number(asg.current_activity_position || 0)));
  const typeLabel = QUIZ_TYPE_LABEL[asg.quiz_type] || 'Quiz';

  if (status === 'completed') {
    const grade = asg.final_score;
    return (
      <div data-testid={`quiz-card-${asg.id}`} className="rounded-2xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-200 p-4 sm:p-5 flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-emerald-500 text-white grid place-items-center shrink-0"><Trophy className="w-5 h-5"/></div>
          <div className="flex-1 min-w-0">
            <div data-testid={`quiz-card-title-${asg.id}`} className="font-display font-extrabold text-lg text-slate-900 truncate">{title}</div>
            <div className="text-xs text-emerald-700 font-semibold mt-0.5 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5"/> Completado</div>
          </div>
          {grade != null && (
            <div data-testid={`quiz-card-grade-${asg.id}`} className="text-right shrink-0">
              <div className="text-3xl font-display font-black text-amber-500 leading-none">⭐ {Number(grade).toFixed(0)}</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">calificación</div>
            </div>
          )}
        </div>
        <Button data-testid={`quiz-card-view-${asg.id}`} onClick={() => onAction('view', asg)} variant="outline" className="w-full border-emerald-300 text-emerald-800 hover:bg-emerald-50">
          Ver resultado <ChevronRight className="w-4 h-4 ml-1"/>
        </Button>
      </div>
    );
  }

  if (status === 'in_progress') {
    const pct = total > 0 ? Math.round((current / total) * 100) : 0;
    return (
      <div data-testid={`quiz-card-${asg.id}`} className="rounded-2xl bg-white border-2 border-amber-300 p-4 sm:p-5 flex flex-col gap-3 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-amber-500 text-white grid place-items-center shrink-0"><Target className="w-5 h-5"/></div>
          <div className="flex-1 min-w-0">
            <div data-testid={`quiz-card-title-${asg.id}`} className="font-display font-extrabold text-lg text-slate-900 truncate">{title}</div>
            <div className="text-xs text-slate-500 mt-0.5">📝 {typeLabel}</div>
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between text-xs font-mono mb-1">
            <span data-testid={`quiz-card-progress-text-${asg.id}`} className="text-slate-700 font-semibold">Actividad {current} de {total}</span>
            <span className="text-slate-500">{pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div data-testid={`quiz-card-progress-bar-${asg.id}`} className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all" style={{ width: `${pct}%` }}/>
          </div>
        </div>
        <Button data-testid={`quiz-card-continue-${asg.id}`} onClick={() => onAction('continue', asg)} className="w-full bg-amber-500 hover:bg-amber-600 text-white">
          Continuar <Play className="w-4 h-4 ml-1"/>
        </Button>
      </div>
    );
  }

  // pending
  return (
    <div data-testid={`quiz-card-${asg.id}`} className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 flex flex-col gap-3 hover:border-indigo-300 hover:shadow-md transition-all">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-indigo-500 text-white grid place-items-center shrink-0"><Target className="w-5 h-5"/></div>
        <div className="flex-1 min-w-0">
          <div data-testid={`quiz-card-title-${asg.id}`} className="font-display font-extrabold text-lg text-slate-900 truncate">{title}</div>
          <div className="text-xs text-slate-500 mt-0.5">📝 {typeLabel}</div>
        </div>
      </div>
      <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2 text-xs text-indigo-800 flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5"/> {total > 0 ? `${total} ${total === 1 ? 'actividad' : 'actividades'} · Listo para comenzar` : 'Listo para comenzar'}
      </div>
      <Button data-testid={`quiz-card-start-${asg.id}`} onClick={() => onAction('start', asg)} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white">
        Comenzar <Play className="w-4 h-4 ml-1"/>
      </Button>
    </div>
  );
}

function EmptyState({ tab }) {
  const msg = TABS.find(t => t.key === tab)?.empty || 'Nada por aquí.';
  return (
    <div data-testid={`quizzes-empty-${tab}`} className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-10 text-center">
      <div className="text-5xl mb-3">🎯</div>
      <div className="font-display font-extrabold text-xl text-slate-800">¡Todo listo!</div>
      <div className="text-sm text-slate-600 mt-1">{msg}</div>
      {tab === 'pending' && (
        <div className="text-sm text-slate-500 mt-2">¡Sigue aprendiendo y prepárate para el próximo reto! ♟️</div>
      )}
    </div>
  );
}

export default function StudentQuizzes() {
  const [items, setItems] = useState(null); // null=loading
  const [tab, setTab] = useState('pending');
  const nav = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/quiz-assignments/mine');
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const g = { pending: [], in_progress: [], completed: [] };
    (items || []).forEach(a => { (g[a.status] || g.pending).push(a); });
    return g;
  }, [items]);

  const counts = { pending: grouped.pending.length, in_progress: grouped.in_progress.length, completed: grouped.completed.length };

  const handleAction = (kind, asg) => {
    // Paso 4A: "Comenzar" y "Continuar" abren la pantalla de bienvenida
    // + countdown. El Runner real se conecta en 4B.
    if (kind === 'start' || kind === 'continue') {
      nav(`/student/quizzes/${asg.id}`);
      return;
    }
    // Paso 2/4A: "Ver resultado" queda como placeholder hasta Paso 4E.
    if (kind === 'view') {
      toast('🏆 Pantalla de resultado en preparación', { description: 'Muy pronto podrás ver el detalle de tu evaluación.' });
      return;
    }
  };

  const visible = grouped[tab] || [];

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-b from-indigo-50/40 via-white to-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="flex items-start gap-3 mb-5">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 text-white grid place-items-center shrink-0 shadow-lg shadow-indigo-500/20">
            <Target className="w-6 h-6"/>
          </div>
          <div className="min-w-0">
            <h1 data-testid="quizzes-header" className="font-display text-3xl sm:text-4xl font-black text-slate-900 leading-tight">🎯 Mis Quizzes</h1>
            <p className="text-sm sm:text-base text-slate-600 mt-1">¡Aquí están tus retos de ajedrez!</p>
          </div>
        </div>

        {/* Segmented tabs */}
        <div data-testid="quizzes-tabs" className="inline-flex bg-white border border-slate-200 rounded-full p-1 mb-6 shadow-sm max-w-full overflow-x-auto">
          {TABS.map(t => {
            const active = tab === t.key;
            const n = counts[t.key] || 0;
            return (
              <button
                key={t.key}
                data-testid={`quizzes-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-3 sm:px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold whitespace-nowrap transition-all ${
                  active ? 'bg-indigo-600 text-white shadow' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                {t.label} <span className={`ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold ${active ? 'bg-white/25 text-white' : 'bg-slate-100 text-slate-600'}`}>{n}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        {items === null ? (
          <div data-testid="quizzes-loading" className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2"/> Cargando tus retos…
          </div>
        ) : visible.length === 0 ? (
          <EmptyState tab={tab}/>
        ) : (
          <div data-testid="quizzes-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {visible.map(asg => (
              <QuizCard key={asg.id} asg={asg} onAction={handleAction}/>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
